import { useState } from 'react';
import { useHistory } from 'react-router';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';

interface IUseTabSelectorArgs<TKey extends string> {
  defaultActiveKey: TKey;
  allowedKeys?: TKey[];
}

export interface ITabSelector<TKey extends string> {
  defaultActiveKey: TKey;
  activeKey: TKey;
  selectTab: (selectedKey: TKey) => void;
}

export function useTabSelectorWithState<TKey extends string>(
  args: IUseTabSelectorArgs<TKey>,
): ITabSelector<TKey> {
  const { defaultActiveKey, allowedKeys } = args;
  const [activeKey, selectTab] = useState<TKey>(defaultActiveKey);

  return {
    defaultActiveKey,
    activeKey,
    selectTab: (selectedKey) => {
      const nextKey = allowedKeys && !allowedKeys.includes(selectedKey)
        ? defaultActiveKey
        : selectedKey;

      selectTab(nextKey);
    },
  };
}

export function useTabSelectorWithHistory<TKey extends string>(
  args: IUseTabSelectorArgs<TKey> & { replace?: boolean }
): ITabSelector<TKey>  {
  const { defaultActiveKey, allowedKeys, replace } = args;
  const history = useHistory();
  const [query] = useQueryParams({ tab: withDefault(StringParam, defaultActiveKey) });

  let activeKey = defaultActiveKey;

  if (query.tab && (!allowedKeys || allowedKeys.includes(query.tab as TKey))) {
    activeKey = query.tab as TKey;
  }

  return {
    defaultActiveKey,
    activeKey,
    selectTab: (selectedKey) => {
      const nextKey = allowedKeys && !allowedKeys.includes(selectedKey)
        ? defaultActiveKey
        : selectedKey;

      const nextPath = `${history.location.pathname}?tab=${nextKey}`;

      if (replace) {
        history.replace(nextPath);
      } else {
        history.push(nextPath);
      }
    },
  };
}
